import { BarChart3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAdminAnalytics } from "./use-admin-analytics";
import { DealStatusBarChart } from "./deal-status-bar-chart";
import { PartnerPerformanceTable } from "./partner-performance-table";
import { TopAssetsList } from "./top-assets-list";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/analytics/page.tsx.
// Workspace-aggregate analytics (NOT a <PartnerAnalyticsView> reuse —
// that's the per-partner view used in /portal/analytics + admin partner-
// detail). A single GET to /api/admin/analytics returns all three card
// payloads.

export function AdminAnalyticsPage() {
  const query = useAdminAnalytics();

  if (query.isError) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Couldn't load analytics"
        description={query.error?.message ?? "Try refreshing."}
      />
    );
  }

  return (
    <div>
      <header className="mb-7">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Performance
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
          Analytics
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Pipeline health, partner performance, and content engagement at a
          glance.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-xl border bg-card p-5 lg:col-span-2">
          <header className="mb-4">
            <h2 className="font-display text-base font-semibold tracking-tight">
              Pipeline by stage
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              HubSpot deal count across the Sales-pipeline stages.
            </p>
          </header>
          {query.isLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <DealStatusBarChart data={query.data?.dealsByStatus ?? []} />
          )}
        </section>

        <section className="rounded-xl border bg-card p-5">
          <header className="mb-4">
            <h2 className="font-display text-base font-semibold tracking-tight">
              Top content
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Most-viewed assets, all-time.
            </p>
          </header>
          {query.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <TopAssetsList assets={query.data?.topAssets ?? []} />
          )}
        </section>
      </div>

      <section className="mt-6 rounded-xl border bg-card p-5">
        <header className="mb-4">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Partner performance
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Partner-sourced HubSpot deals (attributed, won, total ARR) plus a
            separate count of Bookboost intros sent to each partner.
          </p>
        </header>
        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <PartnerPerformanceTable rows={query.data?.performance ?? []} />
        )}
      </section>
    </div>
  );
}
